import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleSortState,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { TenantPhonePipe } from 'src/app/shared/pipes/tenant-phone.pipe';
import { AppDatePipe } from 'src/app/shared/pipes/app-date.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import type {
  TenantDeletedRow,
  TenantRow,
} from 'src/app/core/services/tenant-onboarding-api.service';
import { clientDetailPath, clientEditPath } from '../client-route.util';
import { ActionMenuPanelDirective } from 'src/app/shared/directives/action-menu-panel.directive';

export type ClientTableRow = TenantRow | TenantDeletedRow;

@Component({
  selector: 'app-manage-client-table',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IconModule,
    HasPermissionPipe,
    TenantPhonePipe,
    AppDatePipe,
    ListModuleTableComponent,
    ListCellDefDirective,
    ActionMenuPanelDirective,
  ],
  templateUrl: './manage-client-table.component.html',
})
export class ManageClientTableComponent {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  @Input() rows: ClientTableRow[] = [];
  @Input() view: 'active' | 'deleted' = 'active';
  @Input() loading = false;
  @Input() emptyMessage = '';
  @Input() openActionMenuKey: string | null = null;
  @Input() sortColumn = '';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';

  @Output() sortChange = new EventEmitter<ListModuleSortState>();
  @Output() toggleMenu = new EventEmitter<{ key: string; event: MouseEvent }>();
  @Output() closeMenu = new EventEmitter<void>();
  @Output() viewDetail = new EventEmitter<ClientTableRow>();
  @Output() edit = new EventEmitter<ClientTableRow>();
  @Output() toggleStatus = new EventEmitter<ClientTableRow>();
  @Output() delete = new EventEmitter<ClientTableRow>();
  @Output() restore = new EventEmitter<ClientTableRow>();
  @Output() permanentDelete = new EventEmitter<ClientTableRow>();
  @Output() resend = new EventEmitter<ClientTableRow>();
  @Output() applyMigration = new EventEmitter<ClientTableRow>();
  @Output() applySeeder = new EventEmitter<ClientTableRow>();

  get columns(): ListModuleColumnDef[] {
    return [
      { id: 'id', header: 'ID', sortable: true, sortKey: 'unique_id', cellClass: 'whitespace-nowrap font-mono text-xs' },
      { id: 'client', header: 'Client', sortable: true, sortKey: 'tenant_name' },
      { id: 'contact', header: 'Contact person', sortable: true, sortKey: 'contact_person' },
      {
        id: 'phone',
        header: 'Phone',
        sortable: true,
        sortKey: 'contact_number',
        cellClass: 'whitespace-nowrap font-mono text-xs',
      },
      {
        id: 'email',
        header: 'Email',
        sortable: true,
        sortKey: 'email',
        cellClass: 'max-w-[200px] truncate text-sm',
      },
      {
        id: 'date',
        header: this.view === 'deleted' ? 'Deleted' : 'Created',
        sortable: true,
        sortKey: this.view === 'deleted' ? 'deleted_at' : 'created_at',
        cellClass: 'whitespace-nowrap text-sm',
      },
      {
        id: 'status',
        header: 'Status',
        align: 'center',
        sortable: true,
        sortKey: 'status',
        cellClass: 'whitespace-nowrap text-xs font-bold',
      },
      { id: 'actions', header: 'Action', align: 'right', cellClass: 'text-end' },
    ];
  }

  actionMenuRowKey(index: number): string {
    return this.view === 'deleted' ? `deleted-${index}` : `active-${index}`;
  }

  clientDetailRoute(row: ClientTableRow): string[] | null {
    return clientDetailPath(row);
  }

  clientEditRoute(row: ClientTableRow): string[] | null {
    return this.view === 'active' ? clientEditPath(row) : null;
  }

  deletedAt(row: ClientTableRow): string | null | undefined {
    return (row as TenantDeletedRow).deleted_at ?? null;
  }

  initials(name: string | null | undefined): string {
    const n = (name ?? '').trim();
    return n ? n.charAt(0).toUpperCase() : '?';
  }

  statusBadgeClass(status: string): Record<string, boolean> {
    return {
      'text-success bg-success-light': status === 'Active',
      'text-secondary bg-secondary-light': status === 'InActive',
      'text-danger bg-danger-light': status === 'Blocked',
      'text-warning bg-warning-light': status === 'Suspended' || status === 'Locked',
    };
  }
}
